// 47107 A7k: help is loaded ON DEMAND, per key, never as the 334 KB catalogue. A component asks for exactly the keys it needs, when the
// user shows intent (hover/focus on an (i), a dwell on a page/tab that still has an undismissed guide, the Guide drawer opening).
// One small GET /api/help?keys=a,b&lang=fr per burst (coalesced 15 ms); the rows (and the keys that have none) are cached in memory and
// in sessionStorage, so a key is fetched at most once per session. The request carries the navigation abort signal (api.js: /help is no
// longer a keep-alive read), so leaving the page cancels it; an aborted or failed read is not remembered and is retried on the next intent.
import api from './api';

const STORE = 'ga.help.v1';
const rows = new Map();       // row key -> row | null (known to have none)
const fetched = new Set();    // `${lang}|${base key}` asked successfully
const inflight = new Map();   // `${lang}|${base key}` -> promise
const subs = new Set();
const stats = { requests: 0, bytes: 0 };
try {
  const j = JSON.parse(window.sessionStorage.getItem(STORE) || 'null');
  if (j) { Object.entries(j.rows || {}).forEach(([k, v]) => rows.set(k, v)); (j.fetched || []).forEach(id => fetched.add(id)); }
} catch (_) { /* no storage: memory only */ }
const persist = () => { try { window.sessionStorage.setItem(STORE, JSON.stringify({ rows: Object.fromEntries(rows), fetched: [...fetched] })); } catch (_) { /* quota */ } };
const notify = () => subs.forEach(fn => { try { fn(); } catch (_) { /* ignore */ } });
export const subscribeHelpRows = fn => { subs.add(fn); return () => subs.delete(fn); };
export function resetHelpCache() { rows.clear(); fetched.clear(); inflight.clear(); queue = []; clearTimeout(timer); timer = null; stats.requests = 0; stats.bytes = 0; try { window.sessionStorage.removeItem(STORE); } catch (_) { /* ignore */ } }   // tests
export const helpStats = () => ({ ...stats });   // measurement hook (page_budget / Playwright)

export function getHelpSync(key) { return rows.get(key) ?? null; }
// The row for the active language, falling back to the base (English) key (HelpIcon convention: `<key>__<lang>`).
export const pickHelp = (key, lang) => (key ? ((lang !== 'en' ? getHelpSync(`${key}__${lang}`) : null) ?? getHelpSync(key)) : null);
export const isHelpFetched = (key, lang = 'en') => fetched.has(`${lang}|${key}`);

let queue = [];   // { key, lang, resolve }
let timer = null;
function flush() {
  timer = null;
  const batch = queue; queue = [];
  const byLang = {};
  batch.forEach(item => { (byLang[item.lang] = byLang[item.lang] || []).push(item); });
  Object.entries(byLang).forEach(([lang, items]) => {
    for (let i = 0; i < items.length; i += 50) {
      const part = items.slice(i, i + 50); const keys = part.map(x => x.key);
      stats.requests += 1;
      api.request(`/help?keys=${encodeURIComponent(keys.join(','))}&lang=${lang}`).then(list => {
        stats.bytes += JSON.stringify(list || []).length;
        (list || []).forEach(r => rows.set(r.key, r));
        keys.forEach(k => { fetched.add(`${lang}|${k}`); if (!rows.has(k)) rows.set(k, null); if (lang !== 'en' && !rows.has(`${k}__${lang}`)) rows.set(`${k}__${lang}`, null); });
        persist(); notify();   // only a success wakes the readers (a failed read must not make a reader ask again in a loop)
      }).catch(() => { /* aborted (page left) or failed: not remembered, asked again on the next intent */ })
        .finally(() => { part.forEach(x => { inflight.delete(`${lang}|${x.key}`); x.resolve(); }); });
    }
  });
}

// requestHelp(keys, lang): resolves when those keys are in the cache (or the read failed / was aborted). Idempotent and cheap to call twice.
export function requestHelp(keys, lang = 'en') {
  const waits = [];
  [...new Set((keys || []).filter(Boolean))].forEach(key => {
    const id = `${lang}|${key}`;
    if (fetched.has(id)) return;
    if (inflight.has(id)) { waits.push(inflight.get(id)); return; }
    let resolve; const p = new Promise(r => { resolve = r; });
    inflight.set(id, p); queue.push({ key, lang, resolve }); waits.push(p);
  });
  if (queue.length && !timer) timer = setTimeout(flush, 15);
  return Promise.all(waits).then(() => undefined);
}

// --- v0.43 active-help registry ------------------------------------------------
// Each mounted HelpIcon registers its key; the page Guide aggregates the currently
// registered keys so it lists exactly the ⓘ tooltips on the current tab, and updates
// as tabs (and their icons) mount/unmount. Refcounted (a key can appear more than once).
const activeHelp = new Map();
const activeExtras = new Map();   // key -> { label?, whereUsed?, text? } — content a tooltip shows beyond its DB entry
const helpListeners = new Set();
function notifyHelp() { helpListeners.forEach(fn => { try { fn(); } catch (_) { /* ignore */ } }); }

// `extra` lets a tooltip that is not (only) a DB help entry show up in the Guide: a merged "Where used" block,
// or a plain-text tooltip. The Guide lists whatever a tooltip shows, so the two never drift apart.
export function registerHelp(key, extra) {
  if (!key) return;
  activeHelp.set(key, (activeHelp.get(key) || 0) + 1);
  if (extra) activeExtras.set(key, extra);
  notifyHelp();
}
export function unregisterHelp(key) {
  if (!key) return;
  const n = (activeHelp.get(key) || 0) - 1;
  if (n <= 0) { activeHelp.delete(key); activeExtras.delete(key); } else activeHelp.set(key, n);
  notifyHelp();
}
export function getActiveHelpExtra(key) { return activeExtras.get(key) || null; }
export function getActiveHelpKeys() { return [...activeHelp.keys()]; }
export function subscribeHelp(fn) { helpListeners.add(fn); return () => helpListeners.delete(fn); }
